import { useState, useRef, useEffect } from 'react'
import { useAIPanel } from '../store/aiPanel'
import { client } from '../api/client'
import { TimelineCard, RankingCard, NotificationCard } from './cards'
import Md from './Md'
import { QuickActionsCard } from './cards/QuickActionsCard'

const PROVIDER_LABEL: Record<string, string> = {
  ollama: '本地大模型',
  openai_compat: '在线大模型',
  none: '纯规则',
}
type CapEntry = { available: boolean; reason: string }
type Caps = { providers: Record<string, CapEntry>; system_default: string }

type ChatCard = { type: string; [k: string]: any }
type ChatResp = { reply: string; cards: ChatCard[]; tool: string; duration_ms: number; degraded?: boolean; actual_provider?: string }

export function AIPanel() {
  const { open, setOpen, context, setContext, pendingPrompt, setPendingPrompt, messages, addMessage, clearMessages } = useAIPanel()
  const [caps, setCaps] = useState<Caps | null>(null)
  const [showSelector, setShowSelector] = useState(false)
  const [provider, setProvider] = useState('')
  const [hint, setHint] = useState('')
  const [actual, setActual] = useState('')
  const [hintOk, setHintOk] = useState(false)

  useEffect(() => {
    if (!open) return
    let prevAvail: boolean | null = null
    const check = async () => {
      try {
        const [capRes, prefRes] = await Promise.all([
          client.get<Caps>('/ai/capabilities'),
          client.get<{ provider: string; is_user_pref: boolean }>('/ai/preference'),
        ])
        setCaps(capRes.data)
        setProvider(prefRes.data.provider)
        const key = prefRes.data.provider === 'none' ? 'rule' : prefRes.data.provider
        const pc = capRes.data.providers[key]
        const nowAvail = !!pc?.available
        const avail = Object.values(capRes.data.providers).filter((x) => x.available).length
        if (!prefRes.data.is_user_pref && avail > 1) setShowSelector(true)
        if (prefRes.data.is_user_pref && !nowAvail) {
          setHint(`你选择的「${PROVIDER_LABEL[prefRes.data.provider]}」暂不可用，已由规则代答`)
          setHintOk(false)
        }
        if (prevAvail === false && nowAvail) {
          setHint(`「${PROVIDER_LABEL[prefRes.data.provider]}」已恢复，已自动切回`)
          setHintOk(true)
        }
        prevAvail = nowAvail
      } catch { /* 探测失败不阻塞对话 */ }
    }
    void check()
    const timer = setInterval(check, 30000)
    return () => clearInterval(timer)
  }, [open])

  function effKey(): string {
    if (!caps || !provider) return provider
    const k = provider === 'none' ? 'rule' : provider
    if (provider !== 'none' && !caps.providers[k]?.available) {
      if (caps.providers['ollama']?.available) return 'ollama'
      if (caps.providers['openai_compat']?.available) return 'openai_compat'
      return 'none'
    }
    return provider
  }

  function isRec(key: string) {
    if (!caps) return false
    for (const k of ['ollama', 'openai_compat', 'none']) {
      const c = caps.providers[k === 'none' ? 'rule' : k]
      if (c?.available) return k === key
    }
    return false
  }

  async function pickProvider(key: string) {
    const cap = caps?.providers[key === 'none' ? 'rule' : key]
    if (!cap?.available) {
      setHint(`该方式未配置：${cap?.reason ?? '未知原因'}，请联系管理员`)
      return
    }
    try {
      await client.post('/ai/preference', { provider: key })
      setProvider(key)
      setShowSelector(false)
      setHint('')
    } catch {
      setHint('保存失败，请重试')
    }
  }
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => { listRef.current?.scrollTo(0, listRef.current.scrollHeight) }, [messages])

  async function send(text?: string, extra?: Record<string, unknown>) {
    const msg = (text ?? input).trim()
    if (!msg || sending) return
    addMessage({ role: 'user', text: msg })
    setInput('')
    setSending(true)
    try {
      const res = await client.post<ChatResp>('/ai/chat', { message: msg, context: extra ? { ...context, ...extra } : context })
      const d = res.data
      if (d.actual_provider) setActual(d.actual_provider)
      addMessage({ role: 'ai', text: d.reply, cards: d.cards, degraded: d.degraded })
    } catch (e: any) {
      addMessage({ role: 'ai', text: '请求失败：' + (e?.response?.data?.message ?? e?.message ?? '未知错误') })
    } finally {
      setSending(false)
    }
  }

  // 铃铛等外部入口注入的待发送话术
  useEffect(() => {
    if (open && pendingPrompt) {
      setPendingPrompt('')
      send(pendingPrompt)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pendingPrompt])

  function renderCard(c: ChatCard, i: number) {
    if (c.type === 'confirm')
      return (
        <div key={i} className="rounded-xl border border-amber-300 bg-amber-50 p-3 my-2 shadow-sm">
          <div className="text-sm font-semibold text-amber-900">{String((c as { title?: string }).title ?? '请确认')}</div>
          <div className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{String((c as { text?: string }).text ?? '')}</div>
          <div className="flex gap-2 mt-3">
            <button onClick={() => send('确认执行', { web_confirm: '1', pend_token: String((c as { token?: string }).token ?? '') })}
              className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700">✅ 确认执行</button>
            <button onClick={() => send('取消', { web_confirm: '0', pend_token: String((c as { token?: string }).token ?? '') })}
              className="px-3 py-1.5 rounded-lg bg-gray-200 text-gray-700 text-sm hover:bg-gray-300">❌ 取消</button>
          </div>
        </div>
      )
    if (c.type === 'quick_actions') return <QuickActionsCard key={i} title={c.title} items={c.items} onPick={(m: string) => send(m)} />
    if (c.type === 'ticket_timeline') return <TimelineCard key={i} items={c.items ?? []} />
    if (c.type === 'sla_ranking') return <RankingCard key={i} items={c.items ?? []} />
    if (c.type === 'notifications')
      return <NotificationCard key={i} items={c.items ?? []} unread_count={c.unread_count}
        onItemClick={(it) => {
          const m = /TK[\w]+/.exec(String(it?.title ?? ''))
          if (m) setContext({ ...useAIPanel.getState().context, ticket_no: m[0] })
        }} />
    return null
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-indigo-600 text-white shadow-lg hover:bg-indigo-700 flex items-center justify-center text-2xl">
        AI
      </button>
    )
  }

  const ctxTicket = (context as any)?.ticket_no as string | undefined

  return (
    <div className="fixed bottom-6 right-6 z-50 w-[400px] h-[600px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-800">GreatOps AI</span>
          <button onClick={() => { setShowSelector(true); listRef.current?.scrollTo(0, 0) }} title="切换 AI 模式"
            className="text-xs text-gray-400 hover:text-indigo-600 underline underline-offset-2">
            当前：{PROVIDER_LABEL[actual || effKey()] ?? '…'}
          </button>
          {ctxTicket && <span className="text-xs bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full">上下文：{ctxTicket}</span>}
        </div>
        <div className="flex gap-2">
          <button onClick={clearMessages} className="text-xs text-gray-400 hover:text-gray-600">清空</button>
          <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
      </div>
      {hint && <div className={`px-4 py-1.5 text-xs border-b ${hintOk ? 'text-green-600 bg-green-50 border-green-100' : 'text-amber-600 bg-amber-50 border-amber-100'}`}>{hint}</div>}
      <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-2">
        {showSelector && caps && (
          <div className="mt-6 px-2">
            <div className="text-sm text-gray-600 mb-3 text-center">选择 AI 模式（之后可随时切换）</div>
            {[
              { key: 'ollama', desc: '数据不出内网，需要本地 GPU' },
              { key: 'openai_compat', desc: '效果最好，需要联网' },
              { key: 'none', desc: '任何环境都能用' },
            ].map((opt) => {
              const cap = caps.providers[opt.key === 'none' ? 'rule' : opt.key]
              const active = effKey() === opt.key
              const disabled = !cap?.available
              return (
                <button key={opt.key} disabled={disabled} onClick={() => pickProvider(opt.key)}
                  className={`w-full text-left border rounded-xl px-4 py-3 mb-2 transition
                    ${active ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-indigo-300'}
                    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <div className="text-sm font-medium text-gray-800">{PROVIDER_LABEL[opt.key]}{active ? ' ✓' : ''}{isRec(opt.key) ? ' ·推荐' : ''}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{disabled ? `不可用：${cap?.reason ?? '未配置'}` : opt.desc}</div>
                </button>
              )
            })}
          </div>
        )}
        {messages.length === 0 && !showSelector && (
          <div className="text-sm text-gray-400 text-center mt-8">
            试试：'谁超时最多' / '我有哪些未读' / 'TK单号 的时间线'
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'flex justify-end my-2' : 'my-2'}>
            {m.role === 'user' ? (
              <div className="bg-indigo-600 text-white text-sm rounded-2xl px-3 py-2 max-w-[80%]">{m.text}</div>
            ) : (
              <div className="max-w-full">
                <Md text={m.text} />
                {m.degraded && <div className="text-[11px] text-amber-500 mt-0.5">所选模式暂不可用，已自动用规则应答</div>}
                {m.cards?.map(renderCard)}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="border-t border-gray-100 p-2 flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send() }}
          placeholder="问点什么…" className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-400" />
        <button onClick={() => send()} disabled={sending}
          className="bg-indigo-600 text-white text-sm rounded-lg px-4 disabled:opacity-50">
          {sending ? '…' : '发送'}
        </button>
      </div>
    </div>
  )
}
